/**
 * PostCard — inline blog-post reference card for use inside MDX posts.
 *
 * Usage in an MDX file (no import needed — registered in BlogMDXProvider):
 *
 *   <PostCard slug="parametric-boxes" />
 *
 * Renders identically to a post entry in the blog index. The post metadata
 * is resolved from the registry at render time. If the slug is not found
 * (e.g. the post is unpublished or the slug is wrong) a subtle fallback card
 * is shown instead of a broken link.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Pin } from 'lucide-react';
import { getPost } from '../postRegistry';
import type { PostMeta } from '../types';

export interface PostCardProps {
  /** Slug of the referenced post, e.g. "parametric-boxes" */
  slug: string;
}

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function PostCard({ slug }: PostCardProps) {
  const [meta, setMeta] = useState<PostMeta | null | 'loading'>('loading');

  useEffect(() => {
    let cancelled = false;
    getPost(slug).then((entry) => {
      if (!cancelled) setMeta(entry?.meta ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Loading skeleton — matches card height to avoid layout shift
  if (meta === 'loading') {
    return (
      <div className="not-prose my-6 h-28 animate-pulse rounded-lg border border-border bg-muted/30" />
    );
  }

  // Post not found / unpublished
  if (meta === null) {
    return (
      <div className="not-prose my-6 rounded-lg border border-dashed border-border bg-muted/20 px-6 py-4 text-sm text-muted-foreground">
        Post not found: <code className="font-mono">{slug}</code>
      </div>
    );
  }

  return (
    <Link
      to={`/blog/${meta.slug}`}
      className={[
        'not-prose group my-6 block rounded-lg border p-6 transition-colors',
        meta.pinned
          ? 'border-primary/30 bg-primary/5 hover:border-primary/60 hover:bg-primary/10'
          : 'border-border bg-card hover:border-primary/50 hover:bg-card/80',
      ].join(' ')}
    >
      {meta.pinned && (
        <div className="mb-3 flex items-center gap-1.5">
          <Pin className="h-3 w-3 fill-primary text-primary" />
          <span className="text-xs font-medium uppercase tracking-widest text-primary/80">
            Pinned
          </span>
        </div>
      )}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-foreground group-hover:text-primary">
            {meta.title}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{meta.summary}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <time
              dateTime={meta.date}
              className="text-xs text-muted-foreground"
            >
              {formatDate(meta.date)}
            </time>
            {meta.readingTime != null && (
              <>
                <span className="text-xs text-muted-foreground" aria-hidden>
                  ·
                </span>
                <span className="text-xs text-muted-foreground">
                  {meta.readingTime} min read
                </span>
              </>
            )}
            {meta.tags && meta.tags.length > 0 && (
              <>
                <span className="text-xs text-muted-foreground" aria-hidden>
                  ·
                </span>
                {meta.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                  >
                    {tag}
                  </span>
                ))}
              </>
            )}
          </div>
        </div>
        <ArrowRight className="mt-1 h-4 w-4 flex-none text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
      </div>
    </Link>
  );
}
